import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Box, Paper, TableContainer, LinearProgress, Typography, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { Header } from "../../../components/global/Header";
import FormListToolbar from "../../../components/global/Toolbar";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from "react-router";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import SearchField from '../../../components/search/Search';
import { useCategoryItem } from './useCategoryItem';
import ErrorNotification from '../../../components/ErrorNotification/ErrorNotification';
import api from "../../../services/api";
import { useNotifier } from "muinotify";
import { exportToExcel } from '../../../components/export/exportToExcel';
import { exportToPdf } from '../../../components/export/exportToPdf';
import { exportToCsv } from '../../../components/export/exportToCsv';
import CategoryItemForm from './CategoryItemForm';
import ProductionCategoryItemDetails from './ProductionCategoryItemDetails';
import useSWR from 'swr';

const ProductionCategoryItem = () => {
  const { notifier } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduction, setSelectedProduction] = useState(null);
  const { category_item, isLoading, error, mutate } = useCategoryItem(searchQuery, selectedProduction?.id);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedCategoryItem, setSelectedCategoryItem] = useState(null);

  const { data: productions } = useSWR('/productions');
  const { data: item_category_types } = useSWR('/production-category-item-type');

  const URL = `production-category-item`;
  const NAV_PATH = `category_item`;

  mutate();

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  function getRowId(row) {
    return row.id;
  }

  useEffect(() => {
    if (productions && productions.length > 0) {
      const savedProduction = localStorage.getItem('selectedProduction');
      if (savedProduction) {
        try {
          const parsedProduction = JSON.parse(savedProduction);
          const production = productions.find(p => p.id === parsedProduction.id);
          if (production) {
            setSelectedProduction(production);
          }
        } catch (e) {
          // Invalid saved production, ignore
        }
      }
    }
  }, [productions]);

  useEffect(() => {
    if (selectedProduction) {
      localStorage.setItem('selectedProduction', JSON.stringify(selectedProduction));
    }
  }, [selectedProduction]);

  const handleProductionChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      setSelectedProduction(null);
    } else {
      const production = productions?.find((p) => p.id === value);
      setSelectedProduction(production || null);
    }
    mutate();
  };

  const handleClear = () => {
    setSearchQuery("");
    mutate();
  };

  let navigate = useNavigate();

  const handlePanelClose = () => {
    setSelectedCategoryItem(null);
  };

  const handleView = (category_item) => {
    setSelectedCategoryItem(category_item);
  };

  const handleEdit = (id) => {
    navigate(`/${NAV_PATH}/update/${id}`);
  };

  const handleAdd = () => {
    navigate(`/${NAV_PATH}/create/`);
  };

  const handleDelete = useCallback((id) => {
    confirmAlert({
      title: 'Επιβεβαίωση διαγραφής',
      message: 'Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτήν την εγγραφή;',
      buttons: [
        {
          label: 'Ναι',
          onClick: async () => {
            try {
              await api.delete(`/${URL}/${id}`);
              notifier("Η εγγραφή διαγράφηκε με επιτυχία.", { variant: "success" });
              await mutate();
            } catch (err) {
              notifier("Failed to delete production. Error: " + err.message, { variant: "error" });
            }
          }
        },
        {
          label: 'Όχι',
        }
      ]
    });
  }, [mutate, notifier]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const flattenData = Array.isArray(category_item)
    ? category_item.map((item) => ({
      ...item,
      prod_descr: item?.prod_descr?.p_descr,
      formattedAmount: formatCurrency(item?.pci_amount),
      pci_type_descr:
        item_category_types?.find((type) => type.id === item.pci_type)?.pcit_descr ||
        null,
    })) : [];

  const columnsToExtract = [
    { field: 'prod_descr', headerName: 'Παραγωγή' },
    { field: 'pci_descr', headerName: 'Περιγραφή' },
    { field: 'pci_type_descr', headerName: 'Τρόπος Τιμολόγησης' },
    { field: 'formattedAmount', headerName: 'Προϋπολογισμός' },
    { field: 'pci_notes', headerName: 'Παρατηρήσεις' },
  ];

  const handleExportToExcel = () => {
    exportToExcel(
      flattenData,
      columnsToExtract,
      `Είδη Δαπάνης`
    );
  };

  const handleExportToPdf = () => {
    exportToPdf(
      flattenData,
      columnsToExtract,
      `Είδη Δαπάνης`
    );
  };

  const handleExportToCsv = () => {
    exportToCsv(
      flattenData,
      columnsToExtract,
      `Είδη Δαπάνης`
    );
  };

  const columns = [
    { field: 'pci_aa', },
    { field: 'prod_descr', headerName: 'Παραγωγή', flex: 0.5, minWidth: 300, maxWidth: 500, headerAlign: 'center', },
    { field: 'pci_descr', headerName: 'Περιγραφή', flex: 1, minWidth: 300, maxWidth: 300, headerAlign: 'center', },
    {
      field: 'pci_type_descr',
      headerName: 'Τρόπος Τιμολόγησης',
      flex: 1,
      minWidth: 140,
      headerAlign: 'center',
    },
    {
      field: 'pci_amount', headerName: 'Προϋπολογισμός', flex: 1, minWidth: 50, maxWidth: 185, headerAlign: 'center', align: 'right',
      renderCell: (params) => (<div>{params.row?.pci_amount ? params.row?.formattedAmount : null}</div>),
    },
    { field: 'pci_notes', headerName: 'Παρατηρήσεις', flex: 1, headerAlign: 'center', },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      headerName: "Actions",
      headerAlign: 'center',
      getActions: (params) => [
        <GridActionsCellItem color="primary" icon={<EditOutlinedIcon />} label="Edit" onClick={() => handleEdit(params.row.id)} />,
        <GridActionsCellItem color="secondary" icon={<DeleteOutlineOutlinedIcon />} label="Delete" onClick={() => handleDelete(params.row.id)} />,
      ],
    },
  ];

  return (
    <Box m="20px" >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="Category Items" module="Productions" subtitle="List of category items" />
      </Box>
      
      {/* Production Filter */}
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel id="prod-label">Παραγωγή</InputLabel>
          <Select
            labelId="prod-label"
            id="prod-id"
            label="Παραγωγή"
            value={selectedProduction?.id || ''}
            onChange={handleProductionChange}
          >
            <MenuItem value="">
              <em>Όλες οι Παραγωγές</em>
            </MenuItem>
            {productions?.map((production) => (
              <MenuItem key={production.id} value={production.id}>
                {production.p_descr}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={selectedCategoryItem ? 8 : 12}>

          <FormListToolbar
            handleAdd={handleAdd}
            handleExportToExcel={handleExportToExcel}
            handleExportToPdf={handleExportToPdf}
            handleExportToCsv={handleExportToCsv}
          />
          <Box
            m="8px 0 0 0"
            height="80vh"
          >
            <Paper elevation={2} style={{ padding: '1rem', height: '100%', width: '100%' }}>
              <SearchField searchQuery={searchQuery} handleSearchChange={handleSearchChange} handleClear={handleClear} ml={2} mr={2} />
              {isLoading && <LinearProgress />}
              {error && <ErrorNotification ml={2} mr={2} error={error} />}
              <TableContainer style={{ height: '87%', width: '100%' }}>
                <DataGrid
                  rows={flattenData || []}
                  columns={columns}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'pci_aa', sort: 'asc' }],
                    },
                  }}
                  getRowId={getRowId}
                  columnVisibilityModel={{
                    pci_aa: false,
                    id: false,
                  }}
                  onRowClick={(params) => setSelectedCategoryItem(params.row)}
                  sx={{
                    '& .MuiDataGrid-cell': {
                      borderRight: '1px solid #ddd',
                      borderBottom: 'none',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      borderRight: '1px solid #ddd',
                    },
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnsContainer': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '1px solid #e0e0e0',
                    },
                    '& .MuiTablePagination-root': {
                      color: '#000',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                  }}
                />
              </TableContainer>
            </Paper>
          </Box>
        </Grid>
        {selectedCategoryItem && (
          <Grid item xs={4}>
            <ProductionCategoryItemDetails
              category_item={selectedCategoryItem}
              onClose={handlePanelClose} />
          </Grid>
        )}
      </Grid>
    </Box>

  );
};

export default ProductionCategoryItem;
